import * as Sentry from '@sentry/react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, FormControlLabel, Switch, Tooltip, Typography } from '@mui/material';

import { useContext, useEffect, useState } from 'react';

import StoreContext from 'contexts/storeContext';
import { ExpandMore, InfoOutlined, Receipt } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import InvoicesTable from 'components/InvoicesTable';
import ApiContext from 'contexts/apiContext';
import { Balance } from 'types/balance';
import Spinner from 'common/Spinner';
import { GREEN_BP, GREEN_LIGHT } from 'theme';
import ErrorContext from 'contexts/errorContext';

export default function BillingScreen() {
  const { api } = useContext(ApiContext);
  const { store, formatNumber } = useContext(StoreContext);
  const { setError } = useContext(ErrorContext);
  const [balance, setBalance] = useState<Balance>({ balance: 0 });
  const [loading, setLoading] = useState(true);
  const [showUnpaidOnly, setShowUnpaidOnly] = useState(false);

  useEffect(() => {
    api.balance
      .get(store.id)
      .then((res) => setBalance(res.data))
      .finally(() => setLoading(false))
      .catch((err) => {
        setError({ message: 'Ocurrió un error al obtener el balance de tu tienda. Por favor intenta de nuevo en unos minutos.' });
        console.error(err);
        Sentry.captureException(err);
      });
  }, [api, store, setError]);

  // if balance is negative, it means we owe money to the store
  // but it happens when balance.balance is < 0
  const balanceIsPositive = balance.balance < 0;
  const balanceIsZero = balance.balance === 0;
  const balanceSign = balanceIsZero ? '' : balanceIsPositive ? '+' : '-';
  const balanceValue = Math.abs(balance.balance);

  return (
    <>
      {loading ? (
        <Spinner />
      ) : (
        <>
          <Typography variant='h6' sx={{ my: 2 }}>
            Balance
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Typography
              variant='h5'
              sx={{
                fontWeight: 700,
                color: balanceIsZero ? GREEN_BP : balanceIsPositive ? '#0e7c3a' : '#d32f2f',
              }}
            >
              {balanceSign}${formatNumber(balanceValue)}
            </Typography>
            {balanceIsPositive && (
              <Tooltip
                title='¡Tu balance es positivo! Esto puede pasar si para cierto mes, el dinero a recuperar por tu tienda es mayor a la comisión a pagar. El saldo positivo será descontado automáticamente de tu próxima factura a pagar.'
                enterTouchDelay={0}
              >
                <InfoOutlined fontSize='small' sx={{ ml: 1, color: GREEN_LIGHT }} />
              </Tooltip>
            )}
            {balanceIsZero && (
              <Chip label='¡No tienes cuentas pendientes!' color='success' size='small' sx={{ ml: 2 }} variant='outlined' />
            )}
          </Box>
          <Typography variant='body2'>
            El balance total a pagar toma en cuenta las facturas y notas de crédito emitidas, así como los pagos recibidos.
          </Typography>
        </>
      )}
      <Typography variant='h6' sx={{ my: 2 }}>
        Pagos
      </Typography>
      {store.country.invoice_payment_instructions && (
        <>
          <Accordion defaultExpanded sx={{ mb: 2, borderRadius: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant='body2' sx={{ fontWeight: 600 }}>
                Instrucciones de pago
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, lineHeight: 0.5 }}>
              <Typography variant='caption' whiteSpace='pre-line'>
                {store.country.invoice_payment_instructions}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </>
      )}
      <Link to='receipts'>
        <Button variant='text' color='primary' size='small' startIcon={<Receipt />}>
          Ver recibos y notas de crédito
        </Button>
      </Link>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
        <Typography variant='h6'>
          Facturas
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showUnpaidOnly}
              onChange={(e) => setShowUnpaidOnly(e.target.checked)}
              color='primary'
            />
          }
          label='Solo facturas pendientes'
        />
      </Box>
      <InvoicesTable showUnpaidOnly={showUnpaidOnly} />
    </>
  );
}
